import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Package, LogOut, Users, Truck, MessageSquare, Scale,
  BarChart3, Send, ChevronDown, AlertTriangle, CheckCircle,
  Clock, Eye, X, Wallet, DollarSign, Lock, ShieldCheck
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const statusColors = {
  abierto: "bg-amber-100 text-amber-700",
  en_proceso: "bg-blue-100 text-blue-700",
  resuelto: "bg-emerald-100 text-emerald-700",
  cerrado: "bg-zinc-100 text-zinc-700",
};

const statusLabels = {
  abierto: "Abierto",
  en_proceso: "En proceso",
  resuelto: "Resuelto",
  cerrado: "Cerrado",
};

const sobrepesoStatusColors = {
  pendiente: "bg-amber-100 text-amber-700",
  pagado: "bg-emerald-100 text-emerald-700",
  disputado: "bg-blue-100 text-blue-700",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [sobrepesos, setSobrepesos] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [adminShipments, setAdminShipments] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [waRequests, setWaRequests] = useState([]);
  const [balanceDialog, setBalanceDialog] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);

  // Admin login state
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is already logged in as admin
    if (user && user.role === "admin") {
      setIsAdmin(true);
      fetchAll();
    } else {
      // Try to check if stored token is admin
      checkAdminAuth();
    }
  }, [user]);

  const checkAdminAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      if (res.data.role === "admin") {
        setIsAdmin(true);
        fetchAll();
      } else {
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoginLoading(true);
    setAdminLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: adminEmail,
        password: adminPassword,
      });
      if (res.data.user?.role !== "admin") {
        setAdminLoginError("Esta cuenta no tiene permisos de administrador.");
        setAdminLoginLoading(false);
        return;
      }
      // Save token and set user
      localStorage.setItem("token", res.data.token);
      login(res.data.user, res.data.token);
      setIsAdmin(true);
      setLoading(true);
      fetchAll();
    } catch (error) {
      setAdminLoginError(error.response?.data?.detail || "Credenciales incorrectas");
    } finally {
      setAdminLoginLoading(false);
    }
  };

  const fetchAll = async () => {
    try {
      const [statsRes, ticketsRes, sobrepesosRes, rechargesRes, shipmentsRes, usersRes, waRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/tickets`),
        axios.get(`${API}/admin/sobrepesos`),
        axios.get(`${API}/admin/recharges`),
        axios.get(`${API}/admin/shipments`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/whatsapp-requests`),
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data.tickets || []);
      setSobrepesos(sobrepesosRes.data.sobrepesos || []);
      setRecharges(rechargesRes.data.recharges || []);
      setAdminShipments(shipmentsRes.data.shipments || []);
      setAdminUsers(usersRes.data.users || []);
      setWaRequests(waRes.data.requests || []);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        setIsAdmin(false);
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setReplyLoading(true);
    try {
      await axios.post(`${API}/admin/tickets/${selectedTicket.ticket_id}/respond`, {
        message: replyMessage,
        is_admin: true
      });
      toast.success("Respuesta enviada");
      setReplyMessage("");
      fetchAll();
      // Refresh the selected ticket
      const updated = await axios.get(`${API}/admin/tickets`);
      const refreshed = (updated.data.tickets || []).find(t => t.ticket_id === selectedTicket.ticket_id);
      if (refreshed) setSelectedTicket(refreshed);
    } catch (error) {
      toast.error("Error al responder");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/status?status=${newStatus}`);
      toast.success(`Estado actualizado a: ${statusLabels[newStatus]}`);
      fetchAll();
      if (selectedTicket?.ticket_id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleSimulateSobrepeso = async () => {
    try {
      await axios.post(`${API}/admin/sobrepesos/simulate`);
      toast.success("Sobrepeso simulado exitosamente");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al simular sobrepeso");
    }
  };

  const handleApproveRecharge = async (rechargeId) => {
    try {
      await axios.post(`${API}/admin/recharges/${rechargeId}/approve`);
      toast.success("Recarga aprobada. Saldo acreditado al usuario.");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar recarga");
    }
  };

  const handleRejectRecharge = async (rechargeId) => {
    try {
      await axios.post(`${API}/admin/recharges/${rechargeId}/reject`);
      toast.success("Recarga rechazada.");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar recarga");
    }
  };

  const handleAdjustBalance = async () => {
    if (!balanceDialog || !balanceAmount) return;
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${balanceDialog.id}/balance`, {
        amount,
        reason: balanceReason || (amount > 0 ? "Ajuste admin (agregar)" : "Ajuste admin (quitar)")
      });
      toast.success(`Saldo ajustado: ${amount > 0 ? "+" : ""}$${amount.toFixed(2)}`);
      setBalanceDialog(null);
      setBalanceAmount("");
      setBalanceReason("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ajustar saldo");
    }
  };

  const handleWaStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/whatsapp-requests/${requestId}/status?status=${status}`);
      toast.success(`Solicitud marcada como: ${status}`);
      fetchAll();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsAdmin(false);
    navigate("/");
  };

  // ==================== ADMIN LOGIN SCREEN ====================
  if (!isAdmin && !loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-500/30">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-white">Panel Admin</h1>
            <p className="text-zinc-400 mt-2">SuperPack.mx — Acceso restringido</p>
          </div>

          <Card className="border-zinc-800 bg-zinc-800/50 backdrop-blur-sm shadow-2xl">
            <CardContent className="p-6">
              <form onSubmit={handleAdminLogin} className="space-y-5">
                <div>
                  <Label className="text-zinc-300">Correo de administrador</Label>
                  <Input
                    type="email"
                    placeholder="admin@superpack.mx"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    required
                    className="mt-1.5 h-12 bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500 focus:border-violet-500"
                    data-testid="admin-login-email"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Contraseña</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                    className="mt-1.5 h-12 bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500 focus:border-violet-500"
                    data-testid="admin-login-password"
                  />
                </div>

                {adminLoginError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2" data-testid="admin-login-error">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {adminLoginError}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 font-bold text-base shadow-lg shadow-violet-500/25"
                  disabled={adminLoginLoading}
                  data-testid="admin-login-submit"
                >
                  {adminLoginLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Verificando...
                    </div>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Acceder al panel
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-zinc-600 text-sm mt-6">
            ¿Eres cliente? <a href="/" className="text-violet-400 hover:text-violet-300 font-medium">Ir a SuperPack.mx</a>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-zinc-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-zinc-900 text-white border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold font-['Outfit']">SuperPack</span>
                <Badge className="ml-2 bg-violet-600 text-white text-xs">Admin</Badge>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                onClick={() => navigate("/dashboard")}
                data-testid="admin-go-to-user-dash"
              >
                Ver como usuario
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-zinc-800">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-violet-600 text-white text-sm">
                        {user?.name?.charAt(0) || "A"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="text-zinc-600">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-flex">
            <TabsTrigger value="stats" className="flex items-center gap-2" data-testid="admin-tab-stats">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Resumen</span>
            </TabsTrigger>
            <TabsTrigger value="envios" className="flex items-center gap-2" data-testid="admin-tab-envios">
              <Truck className="w-4 h-4" />
              <span className="hidden sm:inline">Envíos</span>
            </TabsTrigger>
            <TabsTrigger value="recargas" className="flex items-center gap-2 relative" data-testid="admin-tab-recargas">
              <Wallet className="w-4 h-4" />
              <span className="hidden sm:inline">Recargas</span>
              {recharges.filter(r => r.status === "pending").length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {recharges.filter(r => r.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="usuarios" className="flex items-center gap-2" data-testid="admin-tab-usuarios">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Usuarios</span>
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2 relative" data-testid="admin-tab-whatsapp">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
              {waRequests.filter(r => r.status === "pendiente").length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
                  {waRequests.filter(r => r.status === "pendiente").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tickets" className="flex items-center gap-2 relative" data-testid="admin-tab-tickets">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Tickets</span>
              {tickets.filter(t => t.status === "abierto").length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {tickets.filter(t => t.status === "abierto").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sobrepesos" className="flex items-center gap-2" data-testid="admin-tab-sobrepesos">
              <Scale className="w-4 h-4" />
              <span className="hidden sm:inline">Sobrepesos</span>
            </TabsTrigger>
          </TabsList>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <StatCard
                icon={<Users className="w-6 h-6" />}
                label="Usuarios registrados"
                value={stats?.total_users || 0}
                color="violet"
              />
              <StatCard
                icon={<Truck className="w-6 h-6" />}
                label="Envíos totales"
                value={stats?.total_shipments || 0}
                color="emerald"
              />
              <StatCard
                icon={<MessageSquare className="w-6 h-6" />}
                label="Tickets totales"
                value={stats?.total_tickets || 0}
                color="blue"
              />
              <StatCard
                icon={<AlertTriangle className="w-6 h-6" />}
                label="Tickets abiertos"
                value={stats?.open_tickets || 0}
                color="amber"
              />
              <StatCard
                icon={<Scale className="w-6 h-6" />}
                label="Sobrepesos totales"
                value={stats?.total_sobrepesos || 0}
                color="red"
              />
              <StatCard
                icon={<Clock className="w-6 h-6" />}
                label="Sobrepesos pendientes"
                value={stats?.pending_sobrepesos || 0}
                color="orange"
              />
              <StatCard
                icon={<Wallet className="w-6 h-6" />}
                label="Recargas totales"
                value={stats?.total_recharges || 0}
                color="blue"
              />
              <StatCard
                icon={<DollarSign className="w-6 h-6" />}
                label="Recargas pendientes"
                value={stats?.pending_recharges || 0}
                color="amber"
              />
              <StatCard
                icon={<DollarSign className="w-6 h-6" />}
                label="Ingresos totales"
                value={`$${(stats?.total_revenue || 0).toFixed(2)}`}
                color="green"
              />
              <StatCard
                icon={<BarChart3 className="w-6 h-6" />}
                label="Ganancia total (38%)"
                value={`$${(stats?.total_margin || 0).toFixed(2)}`}
                color="emerald"
              />
            </div>

            {/* Recent tickets preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  Tickets recientes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">No hay tickets aún</p>
                ) : (
                  <div className="space-y-3">
                    {tickets.slice(0, 5).map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        className="flex items-center justify-between p-3 border border-zinc-200 rounded-lg hover:bg-zinc-50 cursor-pointer transition-colors"
                        onClick={() => { setSelectedTicket(ticket); setActiveTab("tickets"); }}
                        data-testid={`recent-ticket-${ticket.ticket_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{ticket.subject}</p>
                          <p className="text-sm text-zinc-500">{ticket.user_name} - {ticket.user_email}</p>
                        </div>
                        <Badge className={statusColors[ticket.status]}>
                          {statusLabels[ticket.status]}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Envíos Tab with Margin Details */}
          <TabsContent value="envios" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Envíos — Detalle de Margen</h2>
              <Badge className="bg-emerald-100 text-emerald-700">
                Margen: 38%
              </Badge>
            </div>

            {adminShipments.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center text-zinc-500">
                  <Truck className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
                  <p className="text-lg font-medium">Sin envíos registrados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="admin-shipments-table">
                  <thead>
                    <tr className="bg-zinc-100 text-left">
                      <th className="p-3 font-semibold text-zinc-700 rounded-tl-lg">Fecha</th>
                      <th className="p-3 font-semibold text-zinc-700">Paquetería</th>
                      <th className="p-3 font-semibold text-zinc-700">Guía</th>
                      <th className="p-3 font-semibold text-zinc-700">Ruta</th>
                      <th className="p-3 font-semibold text-zinc-700 text-right">Precio cliente</th>
                      <th className="p-3 font-semibold text-zinc-700 text-right">Costo real</th>
                      <th className="p-3 font-semibold text-zinc-700 text-right rounded-tr-lg">Ganancia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminShipments.map((s) => (
                      <tr key={s.shipment_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-ship-${s.shipment_id}`}>
                        <td className="p-3 text-zinc-500 whitespace-nowrap">
                          {s.created_at ? new Date(s.created_at).toLocaleDateString("es-MX", {
                            day: "numeric", month: "short"
                          }) : "—"}
                        </td>
                        <td className="p-3">
                          <span className="font-medium">{s.carrier}</span>
                          <span className="text-xs text-zinc-400 ml-1">{s.service_type}</span>
                        </td>
                        <td className="p-3 font-mono text-xs text-violet-600">{s.tracking_number || "—"}</td>
                        <td className="p-3 text-zinc-500 whitespace-nowrap">{s.origin_zip} → {s.destination_zip}</td>
                        <td className="p-3 text-right font-bold">${s.precio_cliente?.toFixed(2)}</td>
                        <td className="p-3 text-right text-zinc-500">${s.costo_real?.toFixed(2)}</td>
                        <td className="p-3 text-right">
                          <span className="font-bold text-emerald-600">${s.margen?.toFixed(2)}</span>
                          <span className="text-xs text-zinc-400 ml-1">({s.margen_pct}%)</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50 font-bold">
                      <td colSpan={4} className="p-3 text-emerald-800 rounded-bl-lg">TOTALES</td>
                      <td className="p-3 text-right text-emerald-800">
                        ${adminShipments.reduce((sum, s) => sum + (s.precio_cliente || 0), 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right text-emerald-700">
                        ${adminShipments.reduce((sum, s) => sum + (s.costo_real || 0), 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right text-emerald-600 rounded-br-lg">
                        ${adminShipments.reduce((sum, s) => sum + (s.margen || 0), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Recargas SPEI Tab */}
          <TabsContent value="recargas" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Solicitudes de Recarga SPEI</h2>
              <Badge className="bg-blue-100 text-blue-700">
                {recharges.filter(r => r.status === "pending").length} pendientes
              </Badge>
            </div>

            {recharges.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center text-zinc-500">
                  <Wallet className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
                  <p className="text-lg font-medium">Sin solicitudes de recarga</p>
                  <p className="text-sm mt-1">Las solicitudes SPEI de los usuarios aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {recharges.map((rch) => (
                  <Card key={rch.recharge_id} className={
                    rch.status === "pending" ? "border-amber-300 bg-amber-50/30" :
                    rch.status === "approved" ? "border-emerald-300" : "border-red-300"
                  } data-testid={`admin-recharge-${rch.recharge_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={
                              rch.status === "pending" ? "bg-amber-100 text-amber-700" :
                              rch.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                              "bg-red-100 text-red-700"
                            }>
                              {rch.status === "pending" ? "Pendiente" :
                               rch.status === "approved" ? "Aprobada" : "Rechazada"}
                            </Badge>
                            <span className="font-mono text-xs text-zinc-400">{rch.recharge_id}</span>
                          </div>
                          <p className="font-medium">{rch.user_name || "N/A"}</p>
                          <p className="text-sm text-zinc-500">{rch.user_email}</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            {new Date(rch.created_at).toLocaleString("es-MX", {
                              day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-violet-700">${rch.amount?.toFixed(2)}</p>
                          <p className="text-xs text-zinc-500">MXN</p>
                          {rch.status === "pending" && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleApproveRecharge(rch.recharge_id)}
                                data-testid={`approve-recharge-${rch.recharge_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Aprobar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                onClick={() => handleRejectRecharge(rch.recharge_id)}
                                data-testid={`reject-recharge-${rch.recharge_id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Rechazar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="space-y-6">
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Ticket List */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Todos los tickets ({tickets.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[600px] overflow-y-auto">
                      {tickets.length === 0 ? (
                        <p className="text-center text-zinc-500 py-12">No hay tickets</p>
                      ) : (
                        tickets.map((ticket) => (
                          <div
                            key={ticket.ticket_id}
                            className={`p-4 border-b border-zinc-100 cursor-pointer transition-colors hover:bg-violet-50 ${
                              selectedTicket?.ticket_id === ticket.ticket_id ? "bg-violet-50 border-l-4 border-l-violet-600" : ""
                            }`}
                            onClick={() => setSelectedTicket(ticket)}
                            data-testid={`ticket-item-${ticket.ticket_id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate text-sm">{ticket.subject}</p>
                                <p className="text-xs text-zinc-500 mt-1">{ticket.user_name}</p>
                                <p className="text-xs text-zinc-400">
                                  {new Date(ticket.created_at).toLocaleDateString("es-MX", {
                                    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                                  })}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <Badge className={`text-xs ${statusColors[ticket.status]}`}>
                                  {statusLabels[ticket.status]}
                                </Badge>
                                {ticket.responses?.length > 0 && (
                                  <span className="text-xs text-zinc-400">{ticket.responses.length} resp.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Ticket Detail */}
              <div className="lg:col-span-3">
                {selectedTicket ? (
                  <Card>
                    <CardHeader className="border-b">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg" data-testid="ticket-detail-subject">{selectedTicket.subject}</CardTitle>
                          <p className="text-sm text-zinc-500 mt-1">
                            De: {selectedTicket.user_name} ({selectedTicket.user_email})
                          </p>
                          <p className="text-xs text-zinc-400 mt-1">
                            Categoría: {selectedTicket.category} | ID: {selectedTicket.ticket_id}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" data-testid="ticket-status-dropdown">
                                <Badge className={statusColors[selectedTicket.status]}>
                                  {statusLabels[selectedTicket.status]}
                                </Badge>
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              {Object.entries(statusLabels).map(([key, label]) => (
                                <DropdownMenuItem
                                  key={key}
                                  onClick={() => handleStatusChange(selectedTicket.ticket_id, key)}
                                  data-testid={`status-option-${key}`}
                                >
                                  <Badge className={`mr-2 ${statusColors[key]}`}>{label}</Badge>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {/* Messages */}
                      <div className="max-h-[350px] overflow-y-auto p-4 space-y-4">
                        {/* Original message */}
                        <div className="bg-zinc-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Avatar className="w-6 h-6">
                              <AvatarFallback className="bg-zinc-300 text-zinc-700 text-xs">
                                {selectedTicket.user_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-sm">{selectedTicket.user_name}</span>
                            <span className="text-xs text-zinc-400">
                              {new Date(selectedTicket.created_at).toLocaleString("es-MX")}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                        </div>

                        {/* Responses */}
                        {selectedTicket.responses?.map((resp, idx) => (
                          <div
                            key={idx}
                            className={`rounded-lg p-4 ${
                              resp.is_admin
                                ? "bg-violet-50 border border-violet-200 ml-4"
                                : "bg-zinc-50 mr-4"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className={`text-xs ${resp.is_admin ? "bg-violet-600 text-white" : "bg-zinc-300 text-zinc-700"}`}>
                                  {resp.is_admin ? "S" : resp.user_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-sm">
                                {resp.is_admin ? "Soporte SuperPack" : resp.user_name}
                              </span>
                              <span className="text-xs text-zinc-400">
                                {new Date(resp.created_at).toLocaleString("es-MX")}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{resp.message}</p>
                          </div>
                        ))}
                      </div>

                      {/* Reply box */}
                      <div className="border-t p-4">
                        <div className="flex gap-2">
                          <Textarea
                            placeholder="Escribe tu respuesta al usuario..."
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            rows={3}
                            className="flex-1"
                            data-testid="admin-reply-textarea"
                          />
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button
                            onClick={handleReply}
                            disabled={!replyMessage.trim() || replyLoading}
                            className="bg-violet-600 hover:bg-violet-700"
                            data-testid="admin-reply-btn"
                          >
                            {replyLoading ? "Enviando..." : "Enviar respuesta"}
                            <Send className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-20 text-center text-zinc-500">
                      <Eye className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                      <p className="text-lg">Selecciona un ticket para ver los detalles</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Sobrepesos Tab */}
          <TabsContent value="sobrepesos" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Gestión de Sobrepesos</h2>
              <Button
                onClick={handleSimulateSobrepeso}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="simulate-sobrepeso-btn"
              >
                <Scale className="w-4 h-4 mr-2" />
                Simular sobrepeso
              </Button>
            </div>

            {sobrepesos.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center text-zinc-500">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-400" />
                  <p className="text-lg font-medium">Sin sobrepesos registrados</p>
                  <p className="text-sm mt-1">Puedes simular uno para probar la funcionalidad</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sobrepesos.map((sp) => (
                  <Card key={sp.sobrepeso_id} data-testid={`sobrepeso-${sp.sobrepeso_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={sobrepesoStatusColors[sp.status]}>
                              {sp.status === "pendiente" ? "Pendiente" : sp.status === "pagado" ? "Pagado" : "En disputa"}
                            </Badge>
                            <span className="font-mono text-sm text-zinc-500">{sp.tracking_number}</span>
                            <span className="text-sm text-zinc-400">| {sp.carrier}</span>
                          </div>
                          <p className="text-sm text-zinc-500">
                            Usuario: {sp.user_id}
                          </p>
                          <div className="text-sm text-zinc-600 mt-1">
                            <span>Declarado: <strong>{sp.declared_weight}kg</strong></span>
                            <span className="mx-2">|</span>
                            <span>Real: <strong className="text-red-600">{sp.actual_weight}kg</strong></span>
                            <span className="mx-2">|</span>
                            <span>Diferencia: <strong>+{sp.difference}kg</strong></span>
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">
                            {new Date(sp.created_at).toLocaleString("es-MX")}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-red-600">${sp.extra_charge?.toFixed(2)}</p>
                          <p className="text-xs text-zinc-500">Cargo extra</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== USUARIOS TAB ========== */}
          <TabsContent value="usuarios" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="w-5 h-5 text-violet-500" />
                  Usuarios registrados ({adminUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {adminUsers.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No hay usuarios registrados</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-users-table">
                      <thead>
                        <tr className="border-b border-zinc-700 text-zinc-400">
                          <th className="text-left py-3 px-2">Usuario</th>
                          <th className="text-left py-3 px-2">Email</th>
                          <th className="text-left py-3 px-2">Teléfono</th>
                          <th className="text-right py-3 px-2">Saldo</th>
                          <th className="text-center py-3 px-2">Precio</th>
                          <th className="text-left py-3 px-2">Registro</th>
                          <th className="text-center py-3 px-2">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((u) => (
                          <tr key={u.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                            <td className="py-3 px-2 text-white font-medium">{u.full_name || "—"}</td>
                            <td className="py-3 px-2 text-zinc-300">{u.email}</td>
                            <td className="py-3 px-2 text-zinc-400">{u.phone || "—"}</td>
                            <td className="py-3 px-2 text-right">
                              <span className={`font-bold ${u.balance > 0 ? "text-emerald-400" : "text-zinc-500"}`}>
                                ${u.balance?.toFixed(2)}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <Button size="sm" variant="outline"
                                className={`text-xs px-2 h-7 ${u.pricing_type === "custom" ? "text-amber-400 border-amber-600 hover:bg-amber-900" : "text-zinc-400 border-zinc-600 hover:bg-zinc-800"}`}
                                onClick={async () => {
                                  const newType = u.pricing_type === "custom" ? "normal" : "custom";
                                  try {
                                    await axios.put(`${API}/admin/users/${u.id}/pricing`, { pricing_type: newType });
                                    setAdminUsers(prev => prev.map(x => x.id === u.id ? {...x, pricing_type: newType} : x));
                                    toast.success(newType === "custom"
                                      ? `Precio especial activado para ${u.email} (FedEx $190 / Estafeta $210)`
                                      : `Precio normal activado para ${u.email}`);
                                  } catch { toast.error("Error al cambiar precio"); }
                                }}
                                data-testid={`toggle-pricing-${u.id}`}
                              >
                                <DollarSign className="w-3 h-3 mr-1" />
                                {u.pricing_type === "custom" ? "Especial" : "Normal"}
                              </Button>
                            </td>
                            <td className="py-3 px-2 text-zinc-500 text-xs">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString("es-MX") : "—"}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button size="sm" variant="outline"
                                  className="text-emerald-400 border-emerald-600 hover:bg-emerald-900 text-xs px-2 h-7"
                                  onClick={() => { setBalanceDialog(u); setBalanceAmount(""); setBalanceReason(""); }}
                                  data-testid={`adjust-balance-${u.id}`}
                                >
                                  <Wallet className="w-3 h-3 mr-1" /> Saldo
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Balance adjustment dialog */}
            {balanceDialog && (
              <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="balance-dialog">
                <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md">
                  <CardHeader>
                    <CardTitle className="text-white text-base">
                      Ajustar saldo — {balanceDialog.full_name || balanceDialog.email}
                    </CardTitle>
                    <p className="text-zinc-400 text-sm">Saldo actual: <strong className="text-emerald-400">${balanceDialog.balance?.toFixed(2)}</strong></p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-zinc-300">Monto (positivo = agregar, negativo = quitar)</Label>
                      <Input type="number" step="0.01" placeholder="Ej: 500 o -200"
                        value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)}
                        className="mt-1 bg-zinc-800 text-white border-zinc-700"
                        data-testid="balance-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Razón (opcional)</Label>
                      <Input placeholder="Ej: Ajuste por error, bonificación..."
                        value={balanceReason} onChange={(e) => setBalanceReason(e.target.value)}
                        className="mt-1 bg-zinc-800 text-white border-zinc-700"
                        data-testid="balance-reason-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={handleAdjustBalance}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        data-testid="balance-confirm-btn"
                      >
                        Aplicar ajuste
                      </Button>
                      <Button variant="outline" onClick={() => setBalanceDialog(null)}
                        className="text-zinc-400 border-zinc-600">
                        Cancelar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* ========== WHATSAPP TAB ========== */}
          <TabsContent value="whatsapp" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <MessageSquare className="w-5 h-5 text-green-500" />
                  Solicitudes por WhatsApp ({waRequests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {waRequests.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No hay solicitudes por WhatsApp aún</p>
                ) : (
                  <div className="space-y-4">
                    {waRequests.map((req, idx) => (
                      <Card key={req.request_id || idx} className="bg-zinc-800 border-zinc-700" data-testid={`wa-request-${req.request_id}`}>
                        <CardContent className="py-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  req.status === "pendiente" ? "bg-amber-500 text-white" :
                                  req.status === "procesada" ? "bg-emerald-500 text-white" :
                                  req.status === "completada" ? "bg-blue-500 text-white" :
                                  "bg-zinc-600 text-white"
                                }>
                                  {req.status}
                                </Badge>
                                <span className="text-zinc-400 text-xs font-mono">#{req.request_id}</span>
                              </div>
                              <p className="text-zinc-500 text-xs mt-1">
                                {req.created_at ? new Date(req.created_at).toLocaleString("es-MX") : ""}
                              </p>
                            </div>
                            {req.carrier && (
                              <div className="text-right">
                                <p className="text-white font-bold">{req.carrier}</p>
                                <p className="text-emerald-400 font-bold">${req.price?.toFixed(2)}</p>
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="bg-zinc-900 rounded p-3">
                              <p className="text-violet-400 text-xs font-bold mb-1">REMITENTE</p>
                              <p className="text-white">{req.sender_name}</p>
                              <p className="text-zinc-400">{req.sender_phone}</p>
                              <p className="text-zinc-500 text-xs">{req.sender_address}, {req.sender_colonia}</p>
                              <p className="text-zinc-500 text-xs">CP {req.sender_zip} — {req.sender_city}, {req.sender_state}</p>
                            </div>
                            <div className="bg-zinc-900 rounded p-3">
                              <p className="text-orange-400 text-xs font-bold mb-1">DESTINATARIO</p>
                              <p className="text-white">{req.recipient_name}</p>
                              <p className="text-zinc-400">{req.recipient_phone}</p>
                              <p className="text-zinc-500 text-xs">{req.recipient_address}, {req.recipient_colonia}</p>
                              <p className="text-zinc-500 text-xs">CP {req.recipient_zip} — {req.recipient_city}, {req.recipient_state}</p>
                              {req.recipient_references && <p className="text-zinc-600 text-xs mt-1">Ref: {req.recipient_references}</p>}
                            </div>
                          </div>

                          <div className="mt-3 bg-zinc-900 rounded p-2 text-sm">
                            <span className="text-zinc-400">Paquete:</span>{" "}
                            <span className="text-white">{req.package_content}</span>{" — "}
                            <span className="text-zinc-300">{req.package_weight}kg</span>{" "}
                            {req.package_dimensions && <span className="text-zinc-500">({req.package_dimensions} cm)</span>}
                          </div>

                          <div className="flex gap-2 mt-3">
                            {req.status === "pendiente" && (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                                onClick={() => handleWaStatus(req.request_id, "procesada")}>
                                Marcar procesada
                              </Button>
                            )}
                            {req.status === "procesada" && (
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs"
                                onClick={() => handleWaStatus(req.request_id, "completada")}>
                                Marcar completada
                              </Button>
                            )}
                            {req.status !== "cancelada" && req.status !== "completada" && (
                              <Button size="sm" variant="outline" className="text-red-400 border-red-700 hover:bg-red-900 text-xs"
                                onClick={() => handleWaStatus(req.request_id, "cancelada")}>
                                Cancelar
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
};

// Stat card component
const StatCard = ({ icon, label, value, color }) => {
  const colorMap = {
    violet: "bg-violet-100 text-violet-600",
    emerald: "bg-emerald-100 text-emerald-600",
    blue: "bg-blue-100 text-blue-600",
    amber: "bg-amber-100 text-amber-600",
    red: "bg-red-100 text-red-600",
    orange: "bg-orange-100 text-orange-600",
  };

  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorMap[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-3xl font-bold">{value}</p>
            <p className="text-sm text-zinc-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;
